import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppInputProps } from '@/types/common';
import commonUtils, { PASSWORD_REGEX } from '@/utils/common';
import { cognitoService } from '@/services/cognito';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';

const UsePasswordChange = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [hasPasswordUpdated, setHasPasswordUpdated] = useState(false);
  const { user } = useAppSelector(authSelector);

  const onChangePasswordFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target;

    setChangePasswordFields(prevState => {
      const updatedState = [...prevState];
      const fieldIndex = updatedState.findIndex(i => i.name === name);

      updatedState[fieldIndex].value = value.trim();
      updatedState[fieldIndex].isValid = false;
      updatedState[fieldIndex].error = '';

      return updatedState;
    });
  };

  const initialChangePasswordFields = [
    {
      id: 'current_password',
      name: 'currentPassword',
      type: 'password',
      label: 'current_password',
      placeholder: 'current_password',
      shouldTranslate: true,
      value: '',
      error: '',
      isValid: false,
      emitChange: onChangePasswordFieldChange,
    },
    {
      id: 'new_password',
      name: 'newPassword',
      type: 'password',
      label: 'new_password',
      placeholder: 'new_password',
      shouldTranslate: true,
      value: '',
      error: '',
      isValid: false,
      showPasswordRequirements: true,
      emitChange: onChangePasswordFieldChange,
    },
    {
      id: 'new_password_verification',
      name: 'newPasswordVerification',
      type: 'password',
      label: 'new_password_verification',
      placeholder: 'confirmPassword',
      shouldTranslate: true,
      value: '',
      error: '',
      isValid: false,
      emitChange: onChangePasswordFieldChange,
    },
  ] as AppInputProps[];

  const [changePasswordFields, setChangePasswordFields] = useState<AppInputProps[]>(
    initialChangePasswordFields
  );

  const onChangePasswordSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setChangePasswordFields(prevState => {
      const updatedState = [...prevState];

      for (let i = 0; i < updatedState.length; i++) {
        const { id, value } = updatedState[i];

        if (!value) continue;

        const isCurrentPassword = id === 'current_password';
        const isNewPasswordField = id === 'new_password';
        const isConfirmNewPasswordField = id === 'new_password_verification';

        const newPasswordIndex = updatedState.findIndex(field => field.id === 'new_password');
        const confirmNewPasswordIndex = updatedState.findIndex(
          field => field.id === 'new_password_verification'
        );
        const newPasswordValue = updatedState[newPasswordIndex].value;
        const confirmNewPasswordValue = updatedState[confirmNewPasswordIndex].value;

        if (isCurrentPassword || isNewPasswordField) {
          const isPasswordValid = PASSWORD_REGEX.test(value);
          updatedState[i].isValid = isPasswordValid;
          updatedState[i].error = isPasswordValid ? '' : t('invalidPassword');
        }

        if (isConfirmNewPasswordField) {
          const isNewPasswordValid = PASSWORD_REGEX.test(newPasswordValue ?? '');

          if (isNewPasswordValid) {
            const isIdentical = confirmNewPasswordValue === newPasswordValue;
            updatedState[i].isValid = isIdentical;
            updatedState[i].error = isIdentical ? '' : t('passwordsDontMatch');
          } else {
            updatedState[i].isValid = false;
            updatedState[i].error = '';
          }
        }
      }

      return updatedState;
    });
  };

  useEffect(() => {
    const checkPasswordChangeForm = async () => {
      setIsLoading(true);

      try {
        const isValid = changePasswordFields.every(field => field.isValid);

        if (isValid) {
          const currentPasswordField =
            changePasswordFields.find(field => field.id === 'current_password')?.value ?? '';

          const newPasswordField =
            changePasswordFields.find(i => i.id === 'new_password')?.value ?? '';

          const userEmail = localStorage.getItem('userEmail') ?? '';

          const isClassCodeStudent = user?.metadata?.username ? true : false;

          await cognitoService.updatePassword(
            userEmail,
            currentPasswordField,
            newPasswordField,
            isClassCodeStudent
          );

          commonUtils.showToast(t('password_changed_successfully'), {
            autoClose: 2000,
          });
          setHasPasswordUpdated(true);
          setChangePasswordFields(prevState =>
            prevState.map(field => {
              return {
                ...field,
                value: '',
                error: '',
                isValid: false,
              };
            })
          );
        }

        setIsLoading(false);
      } catch (error: any) {
        const errCode = error as string;
        const errMsg = errCode === 'NotAuthorizedException' ? 'invalidUsernameOrPassword' : errCode;

        if (error) {
          commonUtils.showToast(t(errMsg), {
            style: {
              bottom: '80px',
            },
          });
          setIsLoading(false);
        }
      }
    };

    checkPasswordChangeForm();
  }, [changePasswordFields]);

  const resetFields = useCallback(() => {
    setChangePasswordFields(prevState =>
      prevState.map(field => ({ ...field, value: '', error: '', isValid: false }))
    );
  }, []);

  return {
    isLoading,
    hasPasswordUpdated,
    changePasswordFields,
    onChangePasswordSubmit,
    resetFields,
  };
};

export default UsePasswordChange;
